import { useRef, useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { AudienceFilterModal } from '@/features/studies/creation/components/AudienceFilterModal'
import { Search, Check, Loader2, Filter, X, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildPickerDemographicsLine, buildBlurbLine, buildLocationLine, formatGenderForBlurb, inferAudienceType } from '@/lib/utils/twin-blurb'
import { getMatchingTagNames } from '@/lib/utils/tag-matching'
import { TwinSuggestion } from './useComposeModal'
import type { AiFilterCondition } from '@/types/components/audience-filter-modal'
import { AskAiFilterChips } from './AskAiFilterChips'
import { PopulationFilter, FilterCondition } from '@/types/routes/test-results'
import type { ColumnMetadataResult } from '@/types/components/audience-filter-modal'
import type { AudienceFilterModalProps } from '@/types/components/audience-filter-modal'

interface TwinSuggestionsProps {
  twins: Array<TwinSuggestion>
  selectedIds: Array<string>
  onToggle: (personaId: string) => void
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  onLoadMore: () => void
  search: string
  onSearchChange: (query: string) => void
  filters: Record<string, Array<string>>
  onFiltersChange: (filters: Record<string, Array<string>>, rawFilter?: PopulationFilter | null) => void
  populationId: string
  populationName: string
  coverImageUrl: string | null
  audienceType: 'B2B' | 'B2C' | null
  aiChips?: AiFilterCondition[]
  onAiApplyFilters?: (filters: Record<string, Array<string>>, chips: AiFilterCondition[], mode: 'replace' | 'add') => void
  onRemoveAiChip?: (column: string) => void
  onOpenFilterModalPrefilled?: (selections: Record<string, Array<string>>) => void
  totalPersonas?: number
  /** All unfiltered study twins — used by AskAiFilter for client-side match counting */
  studyTwins?: Array<TwinSuggestion> | null
  /** Maps backend column names to actual persona attribute keys (when they differ) */
  columnNameToAttrKey?: Record<string, string>
  /** Pre-derived column metadata for study mode — skips the population columns API call */
  prefetchedFilterData?: ColumnMetadataResult | null
  /** Client-side preview counter for study mode — replaces the population API preview call */
  onPreviewFilter?: AudienceFilterModalProps['onPreviewFilter']
  /** Minimum personas to enable filter. Defaults to 30. Pass 1 for study mode. */
  minPersonas?: number
  /** User-defined audience tags from audience creation */
  audienceTags?: AudienceFilterModalProps['audienceTags']
}

function populationFilterToSimple(filter: PopulationFilter): Record<string, Array<string>> {
  const result: Record<string, Array<string>> = {}

  function extract(f: PopulationFilter) {
    for (const cond of f.conditions) {
      if ('field' in cond) {
        const fc = cond as FilterCondition
        const values = Array.isArray(fc.value) ? fc.value : [fc.value]
        if (!result[fc.field]) result[fc.field] = []
        for (const v of values) {
          if (!result[fc.field].includes(v)) result[fc.field].push(v)
        }
      } else if ('conditions' in cond) {
        extract(cond as PopulationFilter)
      }
    }
  }

  extract(filter)
  return result
}

/** One suggestion row's avatar — its own component so the thumbnail/original
 *  fallback state (finding 7, image compression) is scoped per twin instead
 *  of being called inside the surrounding `.map`. */
function TwinSuggestionAvatar({ imageUrl, displayName }: { imageUrl: string; displayName: string }) {
  const { displayUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 64)

  return (
    <img
      src={displayUrl}
      alt={displayName}
      className="h-10 w-10 rounded-full border border-border object-cover"
      onError={(e) => {
        const target = e.target as HTMLImageElement
        handleImageError(() => {
          target.src = '/images/twin-fallback.png'
        })
      }}
    />
  )
}

export function TwinSuggestions(props: TwinSuggestionsProps) {
  const {
    twins, selectedIds, onToggle, isLoading, isLoadingMore, hasMore, onLoadMore,
    search, onSearchChange, filters, onFiltersChange,
    populationId, populationName, coverImageUrl, audienceType,
    aiChips = [], onAiApplyFilters, onRemoveAiChip, onOpenFilterModalPrefilled, totalPersonas = 0,
    studyTwins,
    columnNameToAttrKey,
    prefetchedFilterData,
    onPreviewFilter,
    minPersonas,
    audienceTags,
  } = props
  const { t } = useTranslation('app')

  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [appliedTagNames, setAppliedTagNames] = useState<Array<string>>([])
  const prevPopulationIdRef = useRef(populationId)
  if (prevPopulationIdRef.current !== populationId) {
    prevPopulationIdRef.current = populationId
    if (appliedTagNames.length > 0) setAppliedTagNames([])
  }
  const activeFilterCount = Object.keys(filters).filter((k) => filters[k]?.length > 0).length + appliedTagNames.length
  const scrollRef = useRef<HTMLDivElement>(null)

  const columnDisplayLabels = useMemo(() => {
    const map = new Map<string, string>()
    // Human-readable labels for canonical resolved-identity columns
    map.set('std_dem_gender', t('compose.gender'))
    map.set('std_dem_age', t('compose.age'))
    map.set('std_geo_country', t('compose.country'))
    if (prefetchedFilterData?.columns) {
      for (const col of prefetchedFilterData.columns) {
        map.set(col.name, col.displayLabel || col.name)
      }
    }
    return map
  }, [prefetchedFilterData, t])

  const activeFilterChips = useMemo(() => {
    const aiChipColumns = new Set(aiChips.map((c) => c.column))
    const chips: Array<{ column: string; label: string; count: number }> = []
    for (const [column, values] of Object.entries(filters)) {
      if (!values || values.length === 0) continue
      if (aiChipColumns.has(column)) continue
      const label = columnDisplayLabels.get(column)
        ?? column.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      chips.push({ column, label, count: values.length })
    }
    return chips
  }, [filters, aiChips, columnDisplayLabels])

  const removeFilterColumn = useCallback((column: string) => {
    const updated = { ...filters }
    delete updated[column]
    onFiltersChange(updated)
  }, [filters, onFiltersChange])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || isLoadingMore || !hasMore) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
    if (nearBottom) onLoadMore()
  }, [isLoadingMore, hasMore, onLoadMore])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const hasScrolledToSelection = useRef(false)
  useEffect(() => {
    if (hasScrolledToSelection.current || isLoading || selectedIds.length === 0) return
    const container = scrollRef.current
    if (!container) return
    const selectedEl = container.querySelector(`[data-testid="compose-twin-card-${selectedIds[0]}"]`)
    if (!selectedEl) return
    hasScrolledToSelection.current = true
    requestAnimationFrame(() => {
      selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }, [isLoading, selectedIds, twins])

  const handleFilterApply = useCallback((filter: PopulationFilter, _count: number, tagNames?: Array<string>) => {
    setAppliedTagNames(tagNames ?? [])
    const simple = populationFilterToSimple(filter)
    onFiltersChange(simple, tagNames && tagNames.length > 0 ? filter : null)
    setFilterModalOpen(false)
  }, [onFiltersChange])

  const handleContinueWithout = useCallback(() => {
    onFiltersChange({}, null)
    setAppliedTagNames([])
    setFilterModalOpen(false)
  }, [onFiltersChange])

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Non-scrolling header — overflow-y-hidden + scrollbar-gutter:stable reserves the
           same gutter width as the scroll container below, keeping cards aligned with the search bar */}
      <div className="shrink-0 overflow-y-hidden pb-3 [scrollbar-gutter:stable]">
        {/* Search + Filter button */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('conversations.searchTwins')}
              aria-label={t('conversations.searchTwinsAria')}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-9 rounded-lg pl-9 text-sm"
              data-testid="compose-twin-search"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilterModalOpen(true)}
            className={cn(
              'h-9 shrink-0 rounded-lg',
              activeFilterCount > 0 && 'border-primary/40 bg-primary/5 text-primary',
            )}
            data-testid="compose-twin-filter"
          >
            <Filter className="mr-1.5 h-3.5 w-3.5" />
            {t('conversations.filter')}
            {activeFilterCount > 0 && (
              <span className="ml-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {/* AI filter chips */}
        <div className="mt-3">
          <AskAiFilterChips chips={aiChips} onRemove={(column) => onRemoveAiChip?.(column)} />
        </div>

        {/* Active filter chips */}
        {(activeFilterChips.length > 0 || appliedTagNames.length > 0) && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {appliedTagNames.length > 0
              ? appliedTagNames.map((name) => (
                  <button
                    key={`tag-${name}`}
                    type="button"
                    onClick={() => {
                      setAppliedTagNames([])
                      onFiltersChange({}, null)
                    }}
                    title={`Segment: ${name} — click to remove`}
                    className="group inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/5"
                  >
                    <Tag className="h-2.5 w-2.5 shrink-0 text-primary" />
                    <span className="max-w-[200px] truncate">{name}</span>
                    <X className="h-2.5 w-2.5 shrink-0 text-muted-foreground transition-colors group-hover:text-destructive" />
                  </button>
                ))
              : activeFilterChips.map((chip) => (
                  <button
                    key={chip.column}
                    type="button"
                    onClick={() => removeFilterColumn(chip.column)}
                    title={`${chip.label} (${chip.count} values) — click to remove`}
                    className="group inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/5"
                  >
                    <span className="max-w-[160px] truncate">{chip.label}</span>
                    <X className="h-2.5 w-2.5 shrink-0 text-muted-foreground transition-colors group-hover:text-destructive" />
                  </button>
                ))
            }
            <button
              type="button"
              onClick={() => {
                setAppliedTagNames([])
                onFiltersChange({}, null)
              }}
              className="ml-1 text-xs text-primary hover:underline"
            >
              {t('conversations.clearAll')}
            </button>
          </div>
        )}
      </div>

      {/* Scrollable twin list — scrollbar-gutter:stable ensures the gutter space matches the header above */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto [scrollbar-gutter:stable]"
      >
        {/* Twin list */}
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-border p-3">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        ) : twins.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Text as="p" size="sm" color="muted">
              {search || activeFilterCount > 0
                ? t('conversations.noTwinsMatch')
                : t('conversations.noTwinsAvailable')}
            </Text>
          </div>
        ) : (
          <div className="space-y-1.5">
            {twins.map((twin) => {
              const isSelected = selectedIds.includes(twin.personaId)
              const displayName = twin.personaName
              const rawAttrs = (twin.attributes ?? {}) as Record<string, string>
              const ri = twin.resolvedIdentity
              const attrs: Record<string, string> = {
                ...rawAttrs,
                ...(ri?.gender && {
                  std_dem_gender: formatGenderForBlurb(ri.gender),
                  Gender: formatGenderForBlurb(ri.gender),
                }),
                ...(ri?.age_range && { std_dem_age: ri.age_range, Age: ri.age_range }),
                ...(ri?.country && { _resolved_country: ri.country, Country: ri.country }),
              }
              const effectiveAudienceType = audienceType ?? inferAudienceType(attrs)
              const isB2B = effectiveAudienceType?.toLowerCase() === 'b2b'
              const subtitleLine = isB2B
                ? [buildBlurbLine(attrs, effectiveAudienceType), buildLocationLine(attrs)].filter(Boolean).join(' · ') || null
                : buildPickerDemographicsLine(attrs)
              const twinTagNames = twin.audienceTagNames ?? getMatchingTagNames(
                (twin.rawAttributes ?? twin.attributes ?? {}) as Record<string, unknown>,
                audienceTags,
              )

              return (
                <button
                  key={twin.personaId}
                  type="button"
                  onClick={() => onToggle(twin.personaId)}
                  data-testid={`compose-twin-card-${twin.personaId}`}
                  className={cn(
                    'relative flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                      : 'border-border hover:border-primary/30 hover:bg-muted/30',
                  )}
                >
                  <div className="relative shrink-0">
                    {twin.imageUrl ? (
                      <TwinSuggestionAvatar imageUrl={twin.imageUrl} displayName={displayName} />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-muted text-xs font-medium text-muted-foreground">
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary">
                        <Check className="h-2.5 w-2.5 text-primary-foreground" />
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <Text as="p" size="sm" weight="medium" className="truncate">
                      {displayName}
                    </Text>
                    {subtitleLine && (
                      <Text as="p" size="xs" color="muted" className="truncate">
                        {subtitleLine}
                      </Text>
                    )}
                    {twinTagNames.length > 0 && (
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {twinTagNames.map((tagName) => (
                          <span
                            key={tagName}
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] leading-tight text-muted-foreground"
                          >
                            {tagName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </button>
              )
            })}

            {isLoadingMore && (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {hasMore && !isLoadingMore && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onLoadMore}
                className="w-full text-xs text-muted-foreground"
              >
                {t('audiences.loadMore')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Filter modal -- reuses the study audience filter (outside scroll container) */}
      <AudienceFilterModal
        key={populationId}
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        populationId={populationId}
        populationName={populationName}
        coverImageUrl={coverImageUrl}
        prefetchedData={prefetchedFilterData ?? null}
        onFilterApply={handleFilterApply}
        onContinueWithout={handleContinueWithout}
        onPreviewFilter={onPreviewFilter}
        minPersonas={minPersonas}
        onAiApplyFilters={onAiApplyFilters}
        activeAiChips={aiChips}
        audienceType={audienceType}
        audienceTags={audienceTags}
      />
    </div>
  )
}
